import React, { useState, useEffect, useMemo } from "react";
import styles from "./depressionTest.module.css";
import { handle_tools_services } from "../../utils/services";

// Move CircularProgress component outside the main component
const CircularProgress = React.memo(
  ({ percentage, score, interpretationTitle, colorClass, animateProgress }) => {
    const radius = 70;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    // Get color class based on score ranges
    const getColorClass = () => {
      if (score >= 0 && score <= 4) return styles.excellentColor;
      if (score >= 5 && score <= 9) return styles.goodColor;
      if (score >= 10 && score <= 14) return styles.moderateColor;
      if (score >= 15 && score <= 19) return styles.needsAttentionColor;
      if (score >= 20) return styles.severeColor;
      return styles.neutralColor; // Fallback
    };

    return (
      <div className={styles.circularProgress}>
        {/* Background circle */}
        <svg className={styles.progressSvg} viewBox="0 0 180 180">
          <circle
            cx="90"
            cy="90"
            r={radius}
            className={styles.progressBackground}
          />
          {/* Progress circle */}
          <circle
            cx="90"
            cy="90"
            r={radius}
            className={`${styles.progressCircle} ${getColorClass()} ${
              animateProgress ? styles.animate : ""
            }`}
            strokeDasharray={circumference}
            strokeDashoffset={animateProgress ? offset : circumference}
            transform="rotate(-90 90 90)"
          />
        </svg>
        {/* Score text */}
        <div className={styles.progressText}>
          <span className={`${styles.percentage} ${getColorClass()}`}>
            {animateProgress ? score : 0}
          </span>
          <span className={`${styles.interpretationTitle} ${colorClass}`}>
            {interpretationTitle}
          </span>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary renders
    return (
      prevProps.percentage === nextProps.percentage &&
      prevProps.score === nextProps.score &&
      prevProps.animateProgress === nextProps.animateProgress
    );
  }
);

const AnxietyDisorderTest = () => {
  const quizData = [
    {
      question: "Feeling nervous, anxious, or on edge.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 1,
    },
    {
      question: "Not being able to stop or control worrying",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 2,
    },
    {
      question: "Worrying too much about different things",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 3,
    },
    {
      question: "Trouble relaxing.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 4,
    },
    {
      question: "Being so restless that it's hard to sit still.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 5,
    },
    {
      question: "Becoming easily annoyed or irritable.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 6,
    },
    {
      question: "Feeling afraid as if something awful might happen",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 7,
    },
  ];

  // State management
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [answerValues, setAnswerValues] = useState(
    Array(quizData.length).fill(null)
  );
  const [showResult, setShowResult] = useState(false);
  const [name, setName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [email, setEmail] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [animateProgress, setAnimateProgress] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Calculate total possible score
  const maxPossibleScore = 21; // 7 questions with 3 points

  // Calculate user's score - memoize to prevent recalculation
  const score = useMemo(() => {
    return answerValues.reduce((sum, value) => sum + (value || 0), 0);
  }, [answerValues]);

  // Calculate score percentage - memoize to prevent recalculation
  const scorePercentage = useMemo(() => {
    return (score / maxPossibleScore) * 100;
  }, [score, maxPossibleScore]);

  // Get interpretation based on score - memoize to prevent recalculation
  const interpretation = useMemo(() => {
    if (score >= 0 && score <= 4) {
      return {
        title: "Minimal Anxiety",
        description:
          "Your results indicate minimal anxiety symptoms. This is within the normal range for most individuals.",
        colorClass: styles.excellent,
      };
    } else if (score >= 5 && score <= 9) {
      return {
        title: "Mild Anxiety",
        description:
          "Your results suggest mild anxiety symptoms. Consider monitoring your stress levels and implementing self-care strategies.",
        colorClass: styles.good,
      };
    } else if (score >= 10 && score <= 14) {
      return {
        title: "Moderate Anxiety",
        description:
          "Your results indicate moderate anxiety symptoms. Consider discussing these symptoms with a healthcare provider.",
        colorClass: styles.moderate,
      };
    } else if (score >= 15 && score <= 19) {
      return {
        title: "Severe Anxiety",
        description:
          "Your results suggest severe anxiety symptoms. Consultation with a mental health professional is recommended.",
        colorClass: styles.needsAttention,
      };
    } else if (score >= 20 && score <= 21) {
      return {
        title: "Extreme Anxiety",
        description:
          "Your results indicate extremely severe anxiety symptoms. Prompt consultation with a mental health professional is strongly recommended.",
        colorClass: styles.severe,
      };
    } else {
      return {
        title: "Invalid Score",
        description:
          "Unable to calculate a valid score. Please ensure all questions are answered.",
        colorClass: styles.neutral,
      };
    }
  }, [score]);

  useEffect(() => {
    if (showResult) {
      // Start animation after results are shown
      setTimeout(() => {
        setAnimateProgress(true);
      }, 300);
    }
  }, [showResult]);

  // Handler for selecting an answer
  const handleAnswerSelect = (option, index) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = option.text;
    setAnswers(newAnswers);

    const newValues = [...answerValues];
    newValues[currentQuestion] = option.score;
    setAnswerValues(newValues);

    // Set transitioning state to trigger animation
    setTransitioning(true);

    // Auto advance to next question after a short delay
    setTimeout(() => {
      if (currentQuestion < quizData.length - 2) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        setShowResult(true);
      }
      // Reset transitioning state after moving to next question
      setTransitioning(false);
    }, 500); // 500ms delay before changing question
  };

  // Navigation handlers
  const handleNextQuestion = () => {
    if (currentQuestion < quizData.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  // Reset quiz
  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers(Array(quizData.length).fill(null));
    setAnswerValues(Array(quizData.length).fill(null));
    setShowResult(false);
    setFormSubmitted(false);
    setAnimateProgress(false);
    setSubmitError(null);
  };

  // Prepare the complete assessment data
  const prepareAssessmentData = () => {
    // Collect detailed responses in the format expected by the details view
    const detailedResponses = quizData.map((question, index) => ({
      question: question.question,
      answer: answers[index] || "No response",
    }));

    // Create the data object in the structure expected by the panel components
    return {
      fullName: name,
      contactNumber: contactNumber,
      email: email,
      submissionDate: new Date().toISOString(),
      score: score,
      responses: detailedResponses,
      comments: "", // Adding this field to match the expected structure in details view
      // Include the detailed data to maintain all the information
      detailedAssessment: {
        totalScore: score,
        maxPossibleScore: maxPossibleScore,
        interpretationCategory: interpretation.title,
        detailedResponses: quizData.map((question, index) => ({
          questionIndex: question.index,
          questionText: question.question,
          answerText: answers[index],
          answerScore: answerValues[index] || 0,
        })),
      },
    };
  };

  // Handle email submission and data saving
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const form_sending_Data = prepareAssessmentData();
      const response = handle_tools_services(form_sending_Data, "anxiety");

      // Set formSubmitted to true to show the thank you message
      setFormSubmitted(true);
    } catch (error) {
      console.error("Error submitting assessment data:", error);
      setSubmitError(
        "There was an error submitting your data. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to determine option style based on selection
  const getOptionClass = (option) => {
    const isSelected = answers[currentQuestion] === option.text;

    if (transitioning && isSelected) {
      return `${styles.option} ${styles.selected} ${styles.transitioning}`;
    }

    return isSelected ? `${styles.option} ${styles.selected}` : styles.option;
  };

  return (
    <>
      <div className={styles.assessmentBanner}>
        <div
          className={[styles.bannerDecoration, styles.decoration1].join(" ")}
        ></div>
        <div
          className={[styles.bannerDecoration, styles.decoration2].join(" ")}
        ></div>

        <div className={styles.bannerContent}>
          <div className={styles.bannerHeader}>
            <h1 className={styles.SelfBannerTitle}>
              Anxiety Disorder Self Assessment
            </h1>
            <p className={styles.SelfBannerTitle}>
              A confidential screening tool to help evaluate your anxiety
              symptoms.
            </p>
          </div>

          <div className={styles.bannerInfo}>
            <p className={styles.bannerDisclaimer}>
              This assessment is based on the Generalized Anxiety Disorder
              (GAD-7) test. It is a screening tool designed to help identify
              anxiety symptoms that may warrant professional attention
            </p>
          </div>
        </div>
      </div>

      <div className={styles.wellnessQuiz}>
        {!showResult ? (
          <div className={styles.quizContainer}>
            <div className="decorative-circles-MN">
              <div className="circle-1-MN"></div>
              <div className="circle-2-MN"></div>
            </div>

            <div className={styles.quizHeader}>
              <p className={styles.questionCounter}>
                Question {currentQuestion + 1} of {quizData.length}
              </p>
              <div className={styles.progressBarContainer}>
                <div
                  className={styles.progressBar}
                  style={{
                    width: `${
                      ((currentQuestion + 1) / quizData.length) * 100
                    }%`,
                  }}
                />
              </div>
            </div>

            <div
              className={`${styles.questionContainer} ${
                transitioning ? styles.fadeOut : styles.fadeIn
              }`}
            >
              <h2 className={styles.questionText}>
                {quizData[currentQuestion].question}
              </h2>

              <div className={styles.optionsContainer}>
                {quizData[currentQuestion].options.map((option, index) => (
                  <div
                    key={index}
                    className={getOptionClass(option)}
                    onClick={() => handleAnswerSelect(option, index)}
                  >
                    <span>{option.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.navigationButtons}>
              <button
                onClick={handlePreviousQuestion}
                disabled={currentQuestion === 0}
                className={
                  currentQuestion === 0
                    ? `${styles.buttonPrevious} ${styles.disabled}`
                    : styles.buttonPrevious
                }
              >
                Previous
              </button>

              {currentQuestion === quizData.length - 1 ? (
                <button
                  onClick={handleNextQuestion}
                  className={styles.buttonNext}
                >
                  Complete
                </button>
              ) : (
                <button
                  onClick={handleNextQuestion}
                  className={styles.buttonPrevious}
                >
                  Skip
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.resultsContainer}>
            <h2 className={styles.resultsTitle}>Assessment Complete</h2>
            <p className={styles.resultsSubtitle}>
              Thank you for completing this Anxiety Disorder screening.
            </p>

            <div className={styles.resultsContent}>
              {/* Left side: Circular progress */}
              <div className={styles.resultsProgress}>
                <CircularProgress
                  percentage={scorePercentage}
                  score={score}
                  interpretationTitle={interpretation.title}
                  colorClass={interpretation.colorClass}
                  animateProgress={animateProgress}
                />
              </div>

              {/* Right side: Score interpretation - with relative position for overlay */}
              <div className={styles.interpretationWrapper}>
                {/* The interpretation content will always be blurred */}
                <div
                  className={`${styles.resultsInterpretation} ${styles.blurredContent}`}
                >
                  <h3
                    className={`${styles.interpretationTitle} ${interpretation.colorClass}`}
                  >
                    {interpretation.title}
                  </h3>
                  <p className={styles.interpretationDescription}>
                    {interpretation.description}
                  </p>
                  <div className={styles.scoreDetails}>
                    <p>
                      Score: {score} out of {maxPossibleScore} points
                    </p>
                  </div>
                  <div className={styles.disclaimer}>
                    <p>
                      This is a clinical tool for assessing anxiety symptoms.
                      However, results should be discussed with a qualified
                      healthcare professional for accurate interpretation based
                      on your full health history and current condition.
                    </p>
                  </div>
                </div>

                {/* Overlay content - either form or thank you message */}
                <div className={styles.interpretationOverlay}>
                  {formSubmitted ? (
                    <div className={styles.formContainer}>
                      <div className={styles.thankyouMN}>
                        <div className={styles.checkiconMN}>✓</div>
                        <h2 className={styles.formtitleMN}>Thank You!</h2>
                        <p className="form-subtitle-MN">
                          Your mental wellness matters. You're not alone in
                          facing anxiety—support is available.
                        </p>
                        <p className="form-subtitle-MN">
                          Your Detailed Report Has Been Successfully Sent To
                          Your Provided Email.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.formContainer}>
                      <h3 className={styles.formTitle}>Get Your Full Report</h3>
                      <p className={styles.formSubtitle}>
                        Enter your details to receive a personalized assessment
                        report with detailed insights and recommendations.
                      </p>

                      {submitError && (
                        <p className={styles.errorMessage}>{submitError}</p>
                      )}

                      <form
                        onSubmit={handleSubmit}
                        className={styles.emailForm}
                      >
                        <div className={styles.formField}>
                          <label htmlFor="name" className={styles.formLabel}>
                            Name
                          </label>
                          <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className={styles.formInput}
                            placeholder="Your name"
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className={styles.formField}>
                          <label
                            htmlFor="contactNumber"
                            className={styles.formLabel}
                          >
                            Contact Number
                          </label>
                          <input
                            type="tel"
                            id="contactNumber"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={contactNumber}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d*$/.test(value)) {
                                setContactNumber(value);
                              }
                            }}
                            required
                            className={styles.formInput}
                            placeholder="Your contact number"
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className={styles.formField}>
                          <label htmlFor="email" className={styles.formLabel}>
                            Email
                          </label>
                          <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className={styles.formInput}
                            placeholder="Your email address"
                            disabled={isSubmitting}
                          />
                        </div>

                        <button
                          type="submit"
                          className={styles.formButton}
                          disabled={isSubmitting}
                        >
                          {isSubmitting
                            ? "Processing..."
                            : "Get My Detailed Report"}
                        </button>

                        <p className={styles.privacyNote}>
                          We respect your privacy and will never share your
                          information with third parties.
                        </p>
                      </form>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button onClick={resetQuiz} className={styles.resetButton}>
              Take Assessment Again
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default AnxietyDisorderTest;
